import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Collection, useProductsContext} from '../Context/Products/ProductsContext';
import {Common, Creator, DetectionType, PageStyleType} from '@taopix/taopix-core';
import {ProductsActions} from '../Context/Products/ProductsActionTypes';
import {
  Theme,
  Heading,
  ThemeName,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
} from '@taopix/taopix-design-system';
import {PageStyleProps} from '@taopix/taopix-core/dist/types/types';
import {ProjectGeneratorConfig} from '@taopix/taopix-core/dist/types/Generator/ProjectGenerator';
import {useTranslation} from 'react-i18next';
import useLangCode from '../../Hooks/useLangCode';

export type CollectionLayout = {
  code: string;
  description: string;
  filterCode: string;
  filterName: string;
  moreInformationURL: string;
  name: string;
  previewImage: string;
  previewImageDevicePixelRatio: number;
  thumbnailImage: string;
  thumbnailImageDevicePixelRatio: number;
  upgradeLayoutImageDevicePixelRatio: number;
  collection: Collection;
  pagestylelist: string[];
  styles?: any[];
};

export type CreateProjectResponse = {
  brandcode: string;
  collectioncode: string;
  collectionname: string;
  collectiontype: number;
  designurl: string;
  groupcode: string;
  groupdata: string;
  layoutname: string;
  productcode: string;
  projectname: string;
  projectref: string;
  result: number;
  resultmessage: string;
  userdata: string;
};

type DetailsDialogStateType = {
  payload: CollectionLayout;
  open: boolean;
};

const LayoutSelector = () => {
  const [layouts, setLayouts] = useState<CollectionLayout[]>([]);
  const [detailsDialogState, setDetailsDialogState] = useState<DetailsDialogStateType>({
    open: false,
    payload: {name: '', description: '', collection: {collectionName: ''}} as any,
  });

  const {state, dispatch} = useProductsContext();
  const {layoutCode, groupCode} = useParams();
  let langCode = useLangCode();
  if (!langCode) {
    langCode = 'en';
  }

  const {t} = useTranslation();

  const getCreatorConfig = useCallback(
    (layout: CollectionLayout): Partial<ProjectGeneratorConfig> => {
      return {
        api: {
          ...(window as any).config,
        },
        assets: [],
        callbackURL: state.urls.callbackUrl,
        groupCode: groupCode as string,
        collectionCode: layout.collection.collectionCode,
        layoutCode: layout.code,
        extraData: btoa(JSON.stringify([])),
        settings: state.productTheming,
        useAssistant: 1,
        lang: langCode as string,
      };
    },
    [state, groupCode, langCode]
  );

  const onFormatPageStyles = useCallback(
    (entry: CollectionLayout) => {
      const styles = entry.pagestylelist.map(p => {
        const parts = p.split(String.fromCharCode(9));
        let url = state.urls.cdnURL;
        if ('' === url) {
          url = state.urls.serverURL;
        }

        // build up the path of the thumbnail path
        const thumbnailPath = `${entry.collection.thumbnailPath}/products/${entry.collection.collectionCode}/${entry.collection.thumbpath}/`;

        return {
          ref: parts[0],
          type: Number(parts[1]),
          categoryName: parts[2],
          emptypictureboxcount: Number(parts[8]),
          hasgrid: Number(parts[9]),
          thumbnailPath: url + thumbnailPath + parts[10],
          excludeFromAutoflow: Number(parts[11]),
          textboxcount: Number(parts[12]),
          portraitPictureBoxCount: Number(parts[13]),
          landscapePictureBoxCount: Number(parts[14]),
          squarePictureBoxCount: Number(parts[15]),
          detection: DetectionType.PageStyle,
          thumbnailRatio: Number(parts[3]) / Number(parts[4]),
          name: String(parts[16] ?? ''),
          alternativeRef: Number(parts[17]) ?? 0,
        };
      });

      const previews: PageStyleProps[] = [];
      let hasCover = false;
      let pageCount = 24;
      styles.forEach(style => {
        if (style.type === PageStyleType.Cover && !hasCover) {
          previews.push(style);
          hasCover = true;
        }

        if (style.type === PageStyleType.Spread && pageCount > 0) {
          previews.push(style);
          pageCount = pageCount - 2;
        }

        if (style.type === PageStyleType.Single && pageCount > 0) {
          previews.push(style);
          pageCount--;
        }
      });

      entry.styles = previews;
      return entry;
    },
    [state.urls.serverURL, state.urls.cdnURL]
  );

  useEffect(() => {
    if (!layoutCode) {
      // navigate to home
      return;
    }

    if (!state.loaded) {
      return;
    }

    dispatch(ProductsActions.setLoading(true));

    fetch(`${window.config.baseURL}/api/product/layouts/${layoutCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        groupCode,
      }),
    })
      .then(response => {
        response
          .json()
          .then(payload => setLayouts(payload.map(collection => onFormatPageStyles(collection))));
      })
      .finally(() => dispatch(ProductsActions.setLoading(false)));
  }, [layoutCode, state.loaded]);

  const onSelectLayout = useCallback(
    async (layout: CollectionLayout) => {
      if (!state.productTheming.enabled) {
        return false;
      }

      dispatch(ProductsActions.setLoading(true));

      const data = await Creator.createProject(getCreatorConfig(layout));
      if (0 === data.result) {
        window.location.href = data.designurl;
      } else {
        dispatch(ProductsActions.setLoading(false));
      }
    },
    [layouts, state]
  );

  const getThumbnail = (product: CollectionLayout, image: keyof CollectionLayout) => {
    if (!product.hasOwnProperty('collection')) {
      return 'https://placehold.co/100x100';
    }
    const thumb = `${window.config.baseURL}${product.collection.thumbnailPath}/products/${product.collection.collectionCode}/${product.collection.thumbpath}/${product[image]}`;
    if ('' === product[image]) {
      return 'https://placehold.co/100x100';
    }
    return thumb;
  };

  return (
    <>
      <Heading className="selectCollectionStep-ContentHeading py-xl text-xxl">
        {t('*:str_TitleSelectTheme')}
      </Heading>
      <section className="selectCollectionStep-CollectionGrid grid-cols-1 grid min500:grid-cols-2 min800:grid-cols-3 min1100:grid-cols-4 gap-lg">
        {layouts.map((product, index) => {
          return (
            <Theme
              element="section"
              name={ThemeName.Section}
              id={`selectCollectionStep-collectionPanel-${index}`}
              key={`selectCollectionStep-collectionPanel-${index}`}
              className="selectCollectionStep-CollectionPanel group/collectionPanel flex flex-col cursor-pointer hover:outline hover:outline-[2px] hover:outline-themeAccentColour20 outline-offset-[-2px]"
              onClick={event => {
                event.stopPropagation();
                setDetailsDialogState(current => {
                  return {
                    ...current,
                    payload: product,
                    open: true,
                  };
                });
              }}
            >
              <Theme
                name={ThemeName.Container}
                id={`selectCollectionStep-collectionPanelImageContainer-${index}`}
                allowBorder={false}
                allowCorners={false}
                className="w-full flex aspect-square relative"
              >
                <img
                  id={`selectCollectionStep-collectionPanelImage-${index}`}
                  src={getThumbnail(product, 'previewImage')}
                  alt={`Collection ${index}`}
                  className="object-cover w-full"
                />
              </Theme>
              <div
                id={`selectCollectionStep-collectionPanelNameContainer-${index}`}
                className="flex p-sm items-center"
              >
                <Heading
                  id={`selectCollectionStep-collectionPanelName-${index}`}
                  size={4}
                  className="flex-1"
                >
                  {Common.getCurrentLocaleString(product.collection.collectionName, false)}
                </Heading>
              </div>
            </Theme>
          );
        })}
      </section>

      <Dialog
        id={'selectCollectionStep-collectionDetailsDialog'}
        open={detailsDialogState.open}
        width={800}
        onClose={() => setDetailsDialogState({...detailsDialogState, open: false})}
        title={Common.getCurrentLocaleString(
          detailsDialogState.payload.collection.collectionName,
          false
        )}
      >
        <DialogContent>
          <div className="flex lg:space-x-lg sm:flex-col sm:space-y-lg">
            <img
              src={getThumbnail(detailsDialogState.payload, 'thumbnailImage')}
              alt={detailsDialogState.payload.name}
              className="object-cover"
              width={450}
            />
            <div>
              <p className="lg:text-[16px]">
                {'' !== detailsDialogState.payload.collection.collectionDescription &&
                  Common.getCurrentLocaleString(
                    detailsDialogState.payload.collection.collectionDescription,
                    false
                  )}
                {'' === detailsDialogState.payload.collection.collectionDescription &&
                  Common.getCurrentLocaleString(detailsDialogState.payload.description, false)}
              </p>
            </div>
          </div>
          <Heading size={4} className="mb-5">
            {t('*:str_TitleExampleLayouts')}
          </Heading>
          <div className="flex flex-row space-x-sm w-full overflow-auto">
            {detailsDialogState.payload.styles?.map((s: any, index: number) => {
              return (
                <img
                  src={s.thumbnailPath}
                  alt={s.name}
                  style={{height: '80px'}}
                  key={`${s.thumbnailPath}-${index}`}
                  className="border"
                />
              );
            })}
          </div>
        </DialogContent>
        <DialogFooter isSmallScreen={false}>
          <Button
            label={t('*:str_ButtonClose')}
            onClick={() => setDetailsDialogState({...detailsDialogState, open: false})}
            buttonStyle="negative"
          />
          <Button
            label={t('*:str_ButtonSelectTheme')}
            buttonStyle="primary"
            onClick={event => {
              onSelectLayout(detailsDialogState.payload);
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default LayoutSelector;
